import { useCallback, useMemo, useState, useId } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { supabase } from "../supabaseClient";
import { useNavigate, Link } from "react-router-dom";
import { Eye, EyeOff, CheckCircle2, AlertCircle } from "lucide-react";
import { useTranslation as useTranslationOrig } from "react-i18next";

type FormInputs = {
  password: string;
  confirmPassword: string;
};

const MIN_LEN = 8;

// ---- i18n safe hook (évite TS2589) + helper qui retourne TOUJOURS une string
type RawT = (key: string, options?: Record<string, unknown>) => any;
const useTranslationSafe = useTranslationOrig as unknown as () => { t: RawT };

export const UpdatePassword = () => {
  const navigate = useNavigate();

  const { t: tRaw } = useTranslationSafe();
  const tr = useCallback(
    (key: string, options?: Record<string, unknown>): string => {
      const out = tRaw(key, options);
      if (typeof out === "string") return out;
      if (Array.isArray(out)) return out.join("");
      return String(out ?? "");
    },
    [tRaw]
  );

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    setError,
  } = useForm<FormInputs>();

  const [submitting, setSubmitting] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const reqId = useId();
  const errPwdId = useId();
  const errConfId = useId();

  const password = watch("password") || "";

  const checks = useMemo(
    () => ({
      len: password.length >= MIN_LEN,
      upper: /[A-Z]/.test(password),
      digit: /\d/.test(password),
    }),
    [password]
  );

  const onSubmit: SubmitHandler<FormInputs> = async ({ password, confirmPassword }) => {
    if (password !== confirmPassword) {
      setError("confirmPassword", {
        message: tr("auth.updatePassword.errors.mismatch", {
          defaultValue: "Les mots de passe ne correspondent pas",
        }),
      });
      return;
    }

    setSubmitting(true);
    const { error } = await supabase.auth.updateUser({ password });
    setSubmitting(false);

    if (error) {
      alert(
        tr("auth.updatePassword.toast.error", {
          defaultValue: "Erreur : {{msg}}",
          msg: error.message,
        })
      );
    } else {
      alert(
        tr("auth.updatePassword.toast.success", {
          defaultValue: "Mot de passe mis à jour avec succès !",
        })
      );
      navigate("/login");
    }
  };

  return (
    <section className="min-h-screen flex items-center justify-center p-6 bg-gray-50 dark:bg-gray-950 text-gray-900 dark:text-gray-100 transition-colors">
      <div className="w-full max-w-md bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl shadow-sm p-6">
        <h1 className="text-2xl font-bold text-orange-600 dark:text-orange-400 mb-6">
          {tr("auth.updatePassword.title", { defaultValue: "🔑 Nouveau mot de passe" })}
        </h1>

        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          {/* Password */}
          <div className="mb-3">
            <label className="block text-sm font-medium mb-1" htmlFor="password">
              {tr("auth.updatePassword.passwordLabel", { defaultValue: "Nouveau mot de passe" })}
            </label>
            <div className="relative">
              <input
                id="password"
                autoFocus
                {...register("password", {
                  required: tr("auth.updatePassword.errors.required", {
                    defaultValue: "Mot de passe requis",
                  }),
                  minLength: {
                    value: MIN_LEN,
                    message: tr("auth.updatePassword.errors.minLength", {
                      defaultValue: "Minimum {{count}} caractères",
                      count: MIN_LEN,
                    }),
                  },
                  pattern: {
                    value: /^(?=.*[A-Z])(?=.*\d).+$/,
                    message: tr("auth.updatePassword.errors.pattern", {
                      defaultValue: "Au moins une majuscule et un chiffre",
                    }),
                  },
                })}
                type={showPwd ? "text" : "password"}
                autoComplete="new-password"
                className="border border-gray-300 dark:border-gray-700 rounded-lg w-full p-3 pr-11
                           bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100
                           placeholder-gray-400 dark:placeholder-gray-500
                           focus:outline-none focus:ring-2 focus:ring-orange-400"
                placeholder={tr("auth.updatePassword.passwordPlaceholder", {
                  defaultValue: "Nouveau mot de passe",
                })}
                aria-invalid={!!errors.password || undefined}
                aria-describedby={`${reqId} ${errors.password ? errPwdId : ""}`.trim()}
              />
              <button
                type="button"
                onClick={() => setShowPwd((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                aria-label={
                  showPwd
                    ? tr("auth.updatePassword.aria.hidePassword", { defaultValue: "Masquer le mot de passe" })
                    : tr("auth.updatePassword.aria.showPassword", { defaultValue: "Afficher le mot de passe" })
                }
              >
                {showPwd ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            <p id={reqId} className="sr-only">
              {tr("auth.updatePassword.hints.requirements", {
                defaultValue: "Mot de passe requis, minimum 8 caractères, une majuscule et un chiffre",
              })}
            </p>
            {errors.password && (
              <p id={errPwdId} className="mt-1 text-red-500 text-sm flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.password.message}
              </p>
            )}
          </div>

          {/* Password hints */}
          <ul className="mb-4 text-xs space-y-1">
            <li className={`flex items-center gap-2 ${checks.len ? "text-green-600" : "text-gray-500"}`}>
              <CheckCircle2 className="w-4 h-4" />
              {tr("auth.updatePassword.hints.minLength", {
                defaultValue: "8 caractères minimum",
              })}
            </li>
            <li className={`flex items-center gap-2 ${checks.upper ? "text-green-600" : "text-gray-500"}`}>
              <CheckCircle2 className="w-4 h-4" />
              {tr("auth.updatePassword.hints.upper", {
                defaultValue: "1 lettre majuscule",
              })}
            </li>
            <li className={`flex items-center gap-2 ${checks.digit ? "text-green-600" : "text-gray-500"}`}>
              <CheckCircle2 className="w-4 h-4" />
              {tr("auth.updatePassword.hints.digit", {
                defaultValue: "1 chiffre",
              })}
            </li>
          </ul>

          {/* Confirm */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1" htmlFor="confirmPassword">
              {tr("auth.updatePassword.confirmLabel", { defaultValue: "Confirmer le mot de passe" })}
            </label>
            <div className="relative">
              <input
                id="confirmPassword"
                {...register("confirmPassword", {
                  required: tr("auth.updatePassword.errors.confirmRequired", {
                    defaultValue: "Confirmation requise",
                  }),
                  validate: (value) =>
                    value === password ||
                    tr("auth.updatePassword.errors.mismatch", {
                      defaultValue: "Les mots de passe ne correspondent pas",
                    }),
                })}
                type={showConfirm ? "text" : "password"}
                autoComplete="new-password"
                className="border border-gray-300 dark:border-gray-700 rounded-lg w-full p-3 pr-11
                           bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100
                           placeholder-gray-400 dark:placeholder-gray-500
                           focus:outline-none focus:ring-2 focus:ring-orange-400"
                placeholder={tr("auth.updatePassword.confirmPlaceholder", {
                  defaultValue: "Confirmer le mot de passe",
                })}
                aria-invalid={!!errors.confirmPassword || undefined}
                aria-describedby={errors.confirmPassword ? errConfId : undefined}
              />
              <button
                type="button"
                onClick={() => setShowConfirm((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                aria-label={
                  showConfirm
                    ? tr("auth.updatePassword.aria.hidePassword", { defaultValue: "Masquer le mot de passe" })
                    : tr("auth.updatePassword.aria.showPassword", { defaultValue: "Afficher le mot de passe" })
                }
              >
                {showConfirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            {errors.confirmPassword && (
              <p id={errConfId} className="mt-1 text-red-500 text-sm flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.confirmPassword.message}
              </p>
            )}
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="bg-orange-600 hover:bg-orange-700 disabled:opacity-60 text-white font-semibold py-3 px-6 rounded-lg w-full
                       focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 transition-colors"
          >
            {submitting
              ? tr("auth.updatePassword.cta.loading", { defaultValue: "Mise à jour..." })
              : tr("auth.updatePassword.cta.submit", { defaultValue: "Mettre à jour le mot de passe" })}
          </button>

          <div className="mt-4 text-center">
            <Link to="/login" className="text-sm text-blue-600 dark:text-blue-400 hover:underline">
              {tr("auth.updatePassword.backToLogin", { defaultValue: "Retour à la connexion" })}
            </Link>
          </div>
        </form>
      </div>
    </section>
  );
};

export default UpdatePassword;
